import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import PageHeader from "../../components/layout/PageHeader";
import BranchSelect from "../../components/ui/BranchSelect";
import useCurrentUser from "../../hooks/useCurrentUser";
import usePermissions from "../../hooks/usePermissions";
import { AttendanceRecords } from "../attendance/AttendancePage";
import OvertimeQueue from "../approvals/components/OvertimeQueue";
import { PeriodPicker } from "../dtr/components/DtrBits";
import { parsePeriodKey } from "../dtr/components/dtrFormat";
import DtrTab from "./DtrTab";
import ExportTab from "./ExportTab";
import "../../components/ui/PageTabs.css";
import "../attendance/attendance.css";
import "../approvals/approvals.css";
import "../dtr/dtr.css";

const TABS = [
  { key: "attendance", label: "Attendance", icon: "bi-person-check" },
  { key: "dtr", label: "DTR Entries", icon: "bi-calendar2-week" },
  { key: "overtime", label: "Overtime", icon: "bi-stopwatch" },
  { key: "export", label: "Export", icon: "bi-box-arrow-up" },
];
// Shared filters stay when switching tabs; everything else is per tab
const SHARED_KEYS = ["branchId", "assignmentType", "search", "period"];

/**
 * HR → Time: the employee's "My Time", for everyone in the user's scope.
 * One filter bar (working company from the top bar, branch, type, search;
 * the cut-off for DTR and Export), tabs for Attendance, DTR (approve each
 * employee's cut-off), Overtime and Export.
 */
const TimePage = () => {
  const user = useCurrentUser();
  const { can, companyWide } = usePermissions();
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter((t) =>
    t.key === "export"
      ? companyWide("dtr")
      : can(t.key === "dtr" ? "dtr" : t.key),
  );
  const tab = tabs.some((t) => t.key === params.get("tab"))
    ? params.get("tab")
    : tabs[0]?.key;

  const branchId = params.get("branchId") || "";
  const assignmentType = params.get("assignmentType") || "";
  const search = params.get("search") || "";
  const periodValue = params.get("period") || "";
  const period = useMemo(() => parsePeriodKey(periodValue), [periodValue]);
  const shared = useMemo(
    () => ({
      companyId: user.companyId || "",
      branchId,
      assignmentType,
      search,
    }),
    [user.companyId, branchId, assignmentType, search],
  );

  const update = useCallback(
    (changes) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([k, v]) =>
            v ? next.set(k, v) : next.delete(k),
          );
          next.delete("page");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const switchTab = (key) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams();
        SHARED_KEYS.forEach((k) => prev.get(k) && next.set(k, prev.get(k)));
        next.set("tab", key);
        return next;
      },
      { replace: false },
    );

  // Search box: type freely, the URL follows after 300ms
  const [searchText, setSearchText] = useState(search);
  useEffect(() => setSearchText(search), [search]);
  useEffect(() => {
    const value = searchText.trim();
    if (value === search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);
  const onPeriod = useCallback((key) => update({ period: key }), [update]);
  const needsPeriod = tab === "dtr" || tab === "export";

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <PageHeader
          title="Attendance & DTR"
          subtitle="Attendance, DTR entries, overtime and export for the employees under you: the same view as My DTR, per employee."
        />

        <nav className="page-tabs" aria-label="Attendance & DTR">
          {tabs.map((t) => (
            <a
              key={t.key}
              href={`?tab=${t.key}`}
              className={tab === t.key ? "active" : ""}
              aria-current={tab === t.key ? "page" : undefined}
              onClick={(e) => {
                e.preventDefault();
                switchTab(t.key);
              }}
            >
              <i className={`bi ${t.icon} me-1`}></i>
              {t.label}
            </a>
          ))}
        </nav>

        {/* Shared filters */}
        <div className="att-filters mb-3">
          {needsPeriod && (
            <PeriodPicker
              value={periodValue}
              onChange={onPeriod}
              id="time-period"
              scope={shared}
            />
          )}
          <div className="position-relative att-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Name or employee no."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search employees"
            />
          </div>
          <div className="att-filter-branch">
            <BranchSelect
              value={branchId}
              onChange={(v) => update({ branchId: v || "" })}
              id="time-branch"
            />
          </div>
          <select
            className="status-select"
            value={assignmentType}
            onChange={(e) => update({ assignmentType: e.target.value })}
            aria-label="Employee type"
          >
            <option value="">Internal and deployed</option>
            <option value="Internal">Internal</option>
            <option value="Deployed">Deployed</option>
          </select>
        </div>

        {!tab && (
          <div className="att-card approval-empty">
            <i className="bi bi-shield-lock"></i>Your role has no access to
            attendance, DTR or overtime.
          </div>
        )}
        {tab === "attendance" && <AttendanceRecords embedded />}
        {tab === "dtr" && <DtrTab period={period} shared={shared} />}
        {tab === "overtime" && <OvertimeQueue shared={shared} />}
        {tab === "export" && <ExportTab period={period} shared={shared} />}
      </div>
    </Layout>
  );
};

export default TimePage;
